import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'
import { useLocale } from '../../contexts/LocaleContext'
import { formatAICost, formatTokens } from '../../lib/aiUsage'
import { Skeleton } from '../ui/skeleton'

interface UsageReport {
    totals?: { cost_usd?: number }
    by_job_type?: { type?: string; cost_usd?: number }[]
    agent_subscription?: { input_tokens?: number; output_tokens?: number; list_cost_usd?: number; runs?: number }
}

/** The 1st of the current month, as YYYY-MM-DD in the viewer's calendar. */
export function monthStart(now = new Date()): string {
    const m = String(now.getMonth() + 1).padStart(2, '0')
    return `${now.getFullYear()}-${m}-01`
}

/** agentSpend: the cost of the by-type rows of agent missions. */
export function agentSpend(report: UsageReport | undefined): number {
    return (report?.by_job_type ?? [])
        .filter((r) => r.type?.startsWith('agent_run:'))
        .reduce((sum, r) => sum + (r.cost_usd ?? 0), 0)
}

function UsageBody({ org }: { org: string }) {
    const { t } = useTranslation('settings')
    const { locale } = useLocale()
    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/jobs/ai-usage', {
        params: { path: { orgCode: org }, query: { from: monthStart() } },
    })
    if (isLoading) return <Skeleton className="h-16 w-full" />
    const report = data as UsageReport | undefined
    const sub = report?.agent_subscription
    return (
        <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4" data-testid="ai-models-usage-total">
                <dt className="text-muted-foreground">{t('ai_models.usage.total')}</dt>
                <dd className="font-medium text-foreground tabular-nums">{formatAICost(report?.totals?.cost_usd ?? 0, locale)}</dd>
            </div>
            <div className="flex justify-between gap-4 pl-4" data-testid="ai-models-usage-agents">
                <dt className="text-muted-foreground">{t('ai_models.usage.agents')}</dt>
                <dd className="text-foreground tabular-nums">{formatAICost(agentSpend(report), locale)}</dd>
            </div>
            <div className="flex justify-between gap-4" data-testid="ai-models-usage-subscription">
                <dt className="text-muted-foreground">{t('ai_models.usage.subscription')}</dt>
                <dd className="text-right text-foreground">
                    {t('ai_models.usage.subscription_value', {
                        input: formatTokens(sub?.input_tokens ?? 0, locale),
                        output: formatTokens(sub?.output_tokens ?? 0, locale),
                        runs: sub?.runs ?? 0,
                        cost: formatAICost(sub?.list_cost_usd ?? 0, locale),
                    })}
                </dd>
            </div>
        </dl>
    )
}

/**
 * The month's AI spend on the AI models page (spec 2026-10-21-19 §6): the
 * total, of which agents billed per token, and the subscription tokens beside
 * it. Behind data.read, the permission of GET /jobs/ai-usage.
 */
export function AgentUsageSection({ org }: { org: string }) {
    const { t } = useTranslation('settings')
    const canRead = useCan('data.read')
    if (!canRead) return null
    return (
        <section className="bg-card rounded-lg border border-border p-6 space-y-4" data-testid="ai-models-usage">
            <div>
                <h2 className="text-base font-semibold text-foreground">{t('ai_models.usage.title')}</h2>
                <p className="text-muted-foreground text-sm mt-1">{t('ai_models.usage.description')}</p>
            </div>
            <UsageBody org={org} />
        </section>
    )
}
